import { Observable } from 'rxjs';
import httpService from './httpService';
import {
  PricingFunction,
  PricingModel,
} from '../data/models/pricing-model.model';
import { ProviderServicePricing } from '../data/models/provider-service-pricing.model';
import { ClientServicePricing } from '../data/models/client-service-pricing.model';
import { TransportationService } from '../data/models/transportation-service.model';
import { TransportationAttribute } from '../data/models/transportation-attribute.model';
import { CommonEntityState } from '../data/models/entity-state';

class AccountingService {
  private readonly apiBase;
  private readonly apiBaseTransport;
  constructor() {
    const { __environment__ } = window as any;
    this.apiBase = __environment__.REACT_APP_ACCOUNTING_API_URL?.trim() || '';
    this.apiBaseTransport =
      __environment__.REACT_APP_TRANSPORT_ACCOUNTING_API_URL?.trim() || '';
  }

  public getPricingFunctions(states?: string[]): Observable<PricingFunction[]> {
    let url = `${this.apiBase}/getPricingFunctions`;
    if (states?.length) {
      url += `&states=${states.join(';')}`;
    }
    return httpService.getJSON(url);
  }

  public getPricingModels(states?: string[]): Observable<PricingModel[]> {
    let url = `${this.apiBase}/getPricingModels`;
    if (states?.length) {
      url += `?states=${states.join(';')}`;
    }
    return httpService.getJSON(url);
  }

  public createPricingModel(
    pricingModel: PricingModel,
  ): Observable<PricingModel> {
    return httpService.postJSON(
      `${this.apiBase}/createPricingModel`,
      pricingModel,
    );
  }

  public updatePricingModel(
    pricingModel: PricingModel,
  ): Observable<PricingModel> {
    return httpService.postJSON(
      `${this.apiBase}/updatePricingModel`,
      pricingModel,
    );
  }

  public deletePricingModel(pricingModelUUID: string): Observable<void> {
    const url = `${this.apiBase}/deletePricingModel`;
    return httpService.postJSON(url, {
      pricingModelUUID,
    });
  }

  public updatePricingModelEffectiveDates(
    pricingModelUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ): Observable<PricingModel> {
    const url = `${this.apiBase}/updatePricingModelEffectiveDates`;
    return httpService.postJSON(url, {
      pricingModelUUID,
      effectiveStartDate,
      effectiveEndDate,
    });
  }

  public getClientServicePricings(
    clientBC: string,
  ): Observable<ClientServicePricing[]> {
    return httpService.getJSON(
      `${this.apiBase}/getClientServicePricings?clientBC=${clientBC}`,
    );
  }

  public createClientServicePricing(
    csp: ClientServicePricing,
  ): Observable<ClientServicePricing> {
    const url = `${this.apiBase}/createClientServicePricing`;
    return httpService.postJSON(url, csp);
  }

  public updateClientServicePricing(
    csp: ClientServicePricing,
  ): Observable<ClientServicePricing> {
    const url = `${this.apiBase}/updateClientServicePricing`;
    return httpService.postJSON(url, csp);
  }

  public deleteClientServicePricing(id: string): Observable<void> {
    const url = `${this.apiBase}/deleteClientServicePricing`;
    return httpService.postJSON(url, { clientServicePricingUUID: id });
  }

  public testClientServicePricing(
    csp: ClientServicePricing,
  ): Observable<ClientServicePricing[]> {
    const url = `${this.apiBase}/testClientServicePricing`;
    return httpService.postJSON(url, csp);
  }

  public updateClientServicePricingEffectiveDates(
    clientServicePricingUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ): Observable<ClientServicePricing> {
    const url = `${this.apiBase}/updateClientServicePricingEffectiveDates`;
    return httpService.postJSON(url, {
      clientServicePricingUUID,
      effectiveStartDate,
      effectiveEndDate,
    });
  }

  public getProviderServicePricings(
    transportationProviderID?: number,
    states?: CommonEntityState[],
  ): Observable<ProviderServicePricing[]> {
    let url = `${this.apiBase}/getProviderServicePricings`;
    if (transportationProviderID) {
      url += `?transportationProviderID=${transportationProviderID}`;
    }
    if (transportationProviderID && states?.length) {
      url += `&states=${states.join(',')}`;
    } else if (states?.length) {
      url += `?states=${states.join(',')}`;
    }
    return httpService.getJSON(url);
  }

  public getTransportationServices(
    states?: string[],
  ): Observable<TransportationService[]> {
    let url = `${this.apiBase}/getTransportationServices`;
    if (states?.length) {
      url += `?states=${states.join(';')}`;
    }
    return httpService.getJSON(url);
  }

  public getTransportationAttributes(
    states?: string[],
  ): Observable<TransportationAttribute[]> {
    let url = `${this.apiBase}/getTransportationAttributes`;
    if (states?.length) {
      url += `?states=${states.join(';')}`;
    }
    return httpService.getJSON(url);
  }

  public createTransportationAttribute(
    transportationAttribute: TransportationAttribute,
  ): Observable<TransportationAttribute> {
    let url = `${this.apiBase}/createTransportationAttribute`;
    return httpService.postJSON(url, transportationAttribute);
  }

  public updateTransportationAttribute(
    transportationAttribute: TransportationAttribute,
  ): Observable<TransportationAttribute> {
    let url = `${this.apiBase}/updateTransportationAttribute`;
    return httpService.postJSON(url, transportationAttribute);
  }

  public deleteTransportationAttribute(
    transportationAttributeUUID: string,
  ): Observable<void> {
    let url = `${this.apiBase}/deleteTransportationAttribute`;
    return httpService.postJSON(url, { transportationAttributeUUID });
  }

  public updateTransportationAttributeEffectiveDates(
    transportationAttributeUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ): Observable<TransportationAttribute> {
    let url = `${this.apiBase}/updateTransportationAttributeEffectiveDates`;
    return httpService.postJSON(url, {
      transportationAttributeUUID,
      effectiveStartDate,
      effectiveEndDate,
    });
  }

  public createTransportationService(
    transportationService: TransportationService,
  ): Observable<TransportationService> {
    let url = `${this.apiBase}/createTransportationService`;
    return httpService.postJSON(url, transportationService);
  }

  public updateTransportationService(
    transportationService: TransportationService,
  ): Observable<TransportationService> {
    let url = `${this.apiBase}/updateTransportationService`;
    return httpService.postJSON(url, transportationService);
  }

  public deleteTransportationService(
    transportationServiceUUID: string,
  ): Observable<void> {
    const url = `${this.apiBase}/deleteTransportationService`;
    return httpService.postJSON(url, {
      transportationServiceUUID,
    });
  }

  public updateTransportationServiceEffectiveDates(
    transportationServiceUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ): Observable<TransportationService> {
    let url = `${this.apiBase}/updateTransportationServiceEffectiveDates`;
    return httpService.postJSON(url, {
      transportationServiceUUID,
      effectiveStartDate,
      effectiveEndDate,
    });
  }

  public getEstimatedTripLegPricing({
    tripLegReferenceID,
    transportationProviderID,
    passengerUUID,
    coverageAreaID,
    providerTransportationServiceUUID,
  }: {
    tripLegReferenceID: string;
    transportationProviderID: number;
    passengerUUID: string;
    coverageAreaID: number;
    providerTransportationServiceUUID: string;
  }): Observable<{ payableAmount: string }> {
    const url = `${this.apiBase}/estimateTripLegPricing`;
    return httpService.postJSON(url, {
      tripLegReferenceID,
      transportationProviderID,
      passengerUUID,
      coverageAreaID,
      providerTransportationServiceUUID,
    });
  }

  public getNeedsReview(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    groupByProvider?: boolean;
  }): Observable<any> {
    let url = `${this.apiBaseTransport}/needs-review`;
    const queryParams: string[] = [];

    if (params?.search) {
      queryParams.push(`search=${encodeURIComponent(params.search)}`);
    }
    if (params?.groupByProvider) {
      queryParams.push(`groupByProvider=true`);
    }
    if (params?.page !== undefined) {
      queryParams.push(`page=${params.page}`);
    }
    if (params?.pageSize !== undefined) {
      queryParams.push(`pageSize=${params.pageSize}`);
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return httpService.getJSON(url);
  }

  public approveReviewInvoice(
    providerInvoiceLineItemID: number,
    reviewStatusBC: string,
    reviewReasonBC: string,
  ): Observable<any> {
    const url = `${this.apiBaseTransport}/needs-review/${providerInvoiceLineItemID}`;
    return httpService.postJSON(url, {
      reviewStatusBC,
      reviewReasonBC,
    });
  }

  public adjustReviewInvoice(
    providerInvoiceLineItemID: number,
    driverID: number,
    onSite: string,
    onBoard: string,
    completed: string,
  ): Observable<any> {
    const url = `${this.apiBaseTransport}/provider-invoice-line-item/${providerInvoiceLineItemID}`;
    return httpService.putJSON(url, {
      driverID,
      onSite,
      onBoard,
      completed,
    });
  }

  public fetchProviderInvoices(params?: {
    search?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Observable<any> {
    let url = `${this.apiBaseTransport}/provider-invoice`;
    const queryParams: string[] = [];

    if (params?.search) {
      queryParams.push(`search=${encodeURIComponent(params.search)}`);
    }
    if (params?.status) {
      queryParams.push(`status=${encodeURIComponent(params.status)}`);
    }
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return httpService.getJSON(url);
  }

  public assignProviderInvoice(
    providerInvoiceID: number,
    userAccountID: number | null,
  ): Observable<any> {
    const url = `${this.apiBaseTransport}/provider-invoice/${providerInvoiceID}/assign`;
    return httpService.putJSON(url, {
      userAccountID,
    });
  }
}

const accountingService = new AccountingService();

export default accountingService;
